import * as XLSX from 'xlsx';

/**
 * Parses an Excel or CSV file and returns headers + data rows
 * @param {File} file - The uploaded file object
 * @returns {Promise<{ headers: string[], data: Object[] }>}
 */
export function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryStr = e.target?.result;
        if (!binaryStr) {
          reject("Could not read file data");
          return;
        }

        const workbook = XLSX.read(binaryStr, { type: 'binary', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const headers = [];
        const data = [];

        // Get headers from first row
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
          const cell = worksheet[cellAddr];
          headers.push(cell?.v?.toString() || `Column${C + 1}`);
        }

        // Loop through remaining rows
        for (let R = 1; R <= range.e.r; ++R) {
          const row = {};
          for (let C = 0; C < headers.length; ++C) {
            const cellAddr = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = worksheet[cellAddr];
            let value = cell?.v;

            // Fix Excel date serials (when stored as numbers but are dates)
            if (
              cell?.t === 'n' &&
              cell?.z &&
              typeof value === 'number' &&
              cell.z.toLowerCase().includes('yy')
            ) {
              const parsed = XLSX.SSF.parse_date_code(value);
              if (parsed) {
                const { y, m, d, H, M, S } = parsed;
                value = new Date(y, m - 1, d, H, M, S).toISOString();
              }
            }

            row[headers[C]] = value ?? '';
          }
          data.push(row);
        }

        resolve({ headers, data });
      } catch (err) {
        reject("Error parsing file: " + err.message);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsBinaryString(file);
  });
}

/**
 * Checks that all required fields are mapped
 * @param {Object} mappings - Mapping object { key: header }
 * @param {string[]} requiredFields - List of required field keys
 * @returns {boolean}
 */
export function validateHeaderMappings(mappings, requiredFields) {
  return requiredFields.every(
    (field) =>
      mappings[field] !== undefined &&
      mappings[field] !== null &&
      mappings[field] !== ''
  );
}